import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearGame } from '@/store/gameSlice';
import { setDebugEnabled, useDebugEnabled } from '@/debug/debugPreference';
import './DebugPanel.css';

interface DebugPanelProps {
  currentPhase?: string;
  gameId?: string;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ currentPhase, gameId }) => {
  const [error, setError] = useState<string | null>(null);
  const isCreating = false;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const debugOn = useDebugEnabled();

  const goToLobby = () => {
    dispatch(clearGame());
    navigate('/lobby');
  };

  if (!debugOn) {
    return null;
  }
  
  const createDebugGame = async (phase: string) => {
    setError(`A ${phase} debug game is not available. Use Go to lobby, then start a mission.`);
  };

  return (
    <div className="phase-debug">
        <div className="debug-content debug-window">
          <div className="debug-header">
            <h3>Debug</h3>
            <button
              className="debug-close"
              onClick={() => setDebugEnabled(false)}
              aria-label="Turn debug off"
            >
              ×
            </button>
          </div>
          
          {error && (
            <div className="debug-error">
              ⚠️ {error}
            </div>
          )}
          
          <div className="debug-section">
            <h4>Quick Start Game</h4>
            <p className="debug-help">
              Create a pre-configured game in any phase (in game order)
            </p>
            
            {/* Phase 1: Lobby */}
            <button 
              onClick={goToLobby}
              className={`debug-button ${currentPhase === 'lobby' ? 'active' : ''}`}
            >
              1️⃣ Go to lobby
            </button>
            
            {/* Phase 2: Name Selection */}
            <button 
              onClick={() => createDebugGame('name_selection')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'name_selection' ? 'active' : ''}`}
            >
              2️⃣ Name Selection
            </button>
            
            {/* Phase 3: Avatar Selection */}
            <button 
              onClick={() => createDebugGame('avatar_selection')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'avatar_selection' ? 'active' : ''}`}
            >
              3️⃣ Avatar Selection
            </button>
            
            {/* Phase 4: Start Game Animation */}
            <button 
              onClick={() => createDebugGame('start_game')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'start_game' ? 'active' : ''}`}
            >
              4️⃣ Start Game Animation
            </button>
            
            {/* Phase 5: Land Claim */}
            <button 
              onClick={() => createDebugGame('land_claim')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'land_claim' ? 'active' : ''}`}
            >
              5️⃣ Land Claim Phase
            </button>
            
            {/* Phase 6: Development */}
            <button 
              onClick={() => createDebugGame('development')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'development' ? 'active' : ''}`}
            >
              6️⃣ Development Phase (Store)
            </button>
            
            {/* Phase 7: Auction */}
            <button 
              onClick={() => createDebugGame('auction')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'auction' ? 'active' : ''}`}
            >
              7️⃣ Auction Phase
            </button>
            
            {/* Phase 8: Production */}
            <button 
              onClick={() => createDebugGame('production')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'production' ? 'active' : ''}`}
            >
              8️⃣ Production Phase
            </button>
            
            {/* Phase 9: Random Event */}
            <button 
              onClick={() => createDebugGame('random_event')}
              disabled={isCreating}
              className={`debug-button ${currentPhase === 'random_event' ? 'active' : ''}`}
            >
              9️⃣ Random Event Phase
            </button>
          </div>
          
          {gameId && (
            <div className="debug-section">
              <h4>Current Game</h4>
              <div className="debug-info">
                <div className="debug-info-row">
                  <span className="debug-label">Game ID:</span>
                  <span className="debug-value">{gameId}</span>
                </div>
                {currentPhase && (
                  <div className="debug-info-row">
                    <span className="debug-label">Phase:</span>
                    <span className="debug-value">{currentPhase}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="debug-section">
            <h4>Display Options</h4>
            <label className="debug-checkbox">
              <input type="checkbox" defaultChecked />
              <span>Show hex coordinates</span>
            </label>
            <label className="debug-checkbox">
              <input type="checkbox" defaultChecked />
              <span>Show timers & counters</span>
            </label>
            <label className="debug-checkbox">
              <input type="checkbox" />
              <span>Show FPS counter</span>
            </label>
          </div>
          
          <div className="debug-section">
            <h4>Reset User Flags</h4>
            <button 
              onClick={() => {
                localStorage.removeItem('rush_seen_deployment_instructions');
                alert('✅ Deployment instructions flag cleared. You will see the tutorial again next time.');
              }}
              className="debug-button secondary"
            >
              🔄 Reset Deployment Tutorial
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem('rush_seen_rover_runaway');
                alert('✅ Rover runaway message flag cleared. You will see the explanation again next time.');
              }}
              className="debug-button secondary"
            >
              🔄 Reset Rover Runaway Message
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem('rush_seen_deployment_success');
                alert('✅ Deployment success message flag cleared. You will see it again on next deployment.');
              }}
              className="debug-button secondary"
            >
              🔄 Reset Deployment Success Message
            </button>
          </div>
          
          <div className="debug-section debug-footer">
            <p className="debug-note">
              Turn this off from Profile, or close this window.
            </p>
          </div>
        </div>
    </div>
  );
};

